import styled from '@emotion/styled';
import { useAssayContext } from '@resistapp/client/contexts/assay-context';
import { useSampleDataContext } from '@resistapp/client/contexts/sample-data-context';
import { useOverviewContext } from '@resistapp/client/contexts/use-overview-context/use-overview-context';
import { OverviewDatum } from '@resistapp/client/data-utils/plot-data/build-overview-line-data';
import { ColorScalePoint, getDynamicRiskScoreLegendConfig } from '@resistapp/client/utils/metric-utils';
import { type OverviewChartConfiguration } from '@resistapp/client/utils/overview-chart-configurations';
import { AbundanceMode } from '@resistapp/common/types';
import { theme } from '../../shared/theme';
import { BaseMapLegend } from './base-map-legend';

export function RiskScoreMapLegend({
  timeLegend,
  inline = false,
  data,
  showInfoIcon = false,
  enableContextualTip = false,
}: {
  timeLegend: React.ReactNode | string;
  inline?: boolean;
  data?: OverviewDatum[];
  showInfoIcon?: boolean;
  enableContextualTip?: boolean;
}) {
  const { activeOverviewConfiguration, activeAbundanceMode, mapData, defaultProcessMode } = useOverviewContext();
  const { queryFilters } = useSampleDataContext();
  const { getGroup, allAssays } = useAssayContext();
  const legendData = data ?? mapData;

  // Get dynamic legend configuration based on map data risk scores
  const { colorScaleConfig, mapLegendTicks, gradientMultiplier } = getDynamicRiskScoreLegendConfig(
    legendData,
    activeOverviewConfiguration.mapLegendTicks || [],
    queryFilters.filters.selectedTargets,
    defaultProcessMode,
    activeAbundanceMode,
    getGroup,
    allAssays,
  );

  // Helper function to generate gradient string using the multiplier from the dynamic config
  const generateGradient = (config: ColorScalePoint[]): string => {
    return config.map(point => `${point.color} ${point.value * gradientMultiplier}%`).join(', ');
  };

  return (
    <BaseMapLegend
      inline={inline}
      showInfoIcon={showInfoIcon}
      enableContextualTip={enableContextualTip}
      timeLegend={timeLegend}
      indicators={
        <ColorScale
          activeAbundanceMode={activeAbundanceMode}
          activeOverviewConfiguration={activeOverviewConfiguration}
          colorScaleConfig={colorScaleConfig}
          generateGradient={generateGradient}
        />
      }
      indexRange={mapLegendTicks.map(e => (
        <div key={e}>{e}</div>
      ))}
    />
  );
}

// Define ColorScale specific to Risk Score, now accepting color config and gradient function as props
const ColorScale = styled.div<{
  activeAbundanceMode: AbundanceMode;
  activeOverviewConfiguration: OverviewChartConfiguration;
  colorScaleConfig: ColorScalePoint[];
  generateGradient: (config: ColorScalePoint[]) => string;
}>`
  width: 100%;
  height: 20px;
  background: linear-gradient(to right, ${props => props.generateGradient(props.colorScaleConfig)});
  border-radius: ${theme.borders.radius.small};
`;
